import { Active, DndContext, Over, UniqueIdentifier } from "@dnd-kit/core"
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"
import * as React from "react"
import { PrimaryButton } from "@humanpredictiveintelligence/myqvt-library"
import { Dialog } from "@material-ui/core"
import { t } from "ttag"

import { CustomQuestion, CustomQuestionWithChoicesPayload } from "models/CustomQuestions"
import { QuestionType } from "models/generated"
import { SortableChoice } from "./components"
import * as Styles from "../QuestionDialog.styles"

export const QuestionClassicDialog: React.FC<QuestionDialogProps> = (props) => {
  const [ name, setName ] = React.useState(props.customQuestion?.name ?? "")
  const [ choices, setChoices ] = React.useState<string[]>(
    props.customQuestion?.choices.map(choice => choice.name) ?? [ "", "" ],
  )

  const isEdition = Boolean(props.customQuestion)
  const isNpsQuestion = props.customQuestion?.type === QuestionType.Nps

  const isDirty = React.useMemo(() => {
    if (!props.customQuestion) {
      return true
    }

    const initialName = props.customQuestion.name
    const initialChoices = props.customQuestion.choices.map(choice => choice.name)

    const hasNameChanged = name !== initialName

    if (isNpsQuestion) {
      return hasNameChanged
    }

    const haveChoicesChanged =
      choices.length !== initialChoices.length ||
      choices.some((choice, index) => choice !== initialChoices[index])

    return hasNameChanged || haveChoicesChanged
  }, [ name, choices, props.customQuestion, isNpsQuestion ])

  const canValidate = () => {
    if (isNpsQuestion) {
      return name.trim().length > 0
    }

    return name.trim().length > 0 &&
    choices.length >= 2 &&
    choices.every(choice => choice.trim().length > 0)
  }

  const getTitle = () => {
    if (isNpsQuestion) {
      return (
        <Styles.Title>
          {isEdition ? t`Edit NPS question` : t`Create NPS question`}
        </Styles.Title>
      )
    }

    return (
      <Styles.Title>
        {isEdition ? t`Edit question` : t`Create question`}
      </Styles.Title>
    )
  }

  return (
    <Dialog
      open
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
    >
      <Styles.Content>
        <Styles.Title>
          {getTitle()}
        </Styles.Title>

        <Styles.Introduction>
          {isNpsQuestion
            ? t`You are about to create an NPS question. This is a recommendation score that will allow 
    users to rate your organization on a scale from 0 to 10.`
            : isEdition
              ? t`You are about to edit a custom question. We encourage you to keep the intent of the survey neutral,
    well-meaning, and not designed to influence the response, so that you can gather the genuine feelings
    of the respondents.`
              : t`You are about to create a custom question. We encourage you to keep the intent of the survey neutral,
    well-meaning, and not designed to influence the response, so that you can gather the genuine feelings
    of the respondents.`}
        </Styles.Introduction>

        <Styles.Field>
          <Styles.Label>{t`Question :`}</Styles.Label>

          <Styles.Input
            value={name}
            onChange={event => setName(event.target.value)}
            disabled={props.isLoading}
          />
        </Styles.Field>

        {!isNpsQuestion && (
          <Styles.ScaleContainer>
            <Styles.NegativeLabel>
              {t`Most negative response`}
            </Styles.NegativeLabel>

            <DndContext onDragEnd={({ active, over }) => reorderChoices(active, over)}>
              <SortableContext
                items={choices.map((_, index) => getChoiceId(index))}
                strategy={verticalListSortingStrategy}
                disabled={props.isLoading}
              >
                {choices.map((choice, index) => (
                  <SortableChoice
                    key={getChoiceId(index)}
                    id={getChoiceId(index)}
                    index={index}
                    choice={choice}
                    canRemove={choices.length >= 3}
                    onChange={updateChoice}
                    onRemove={removeChoice}
                    isLoading={props.isLoading}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {choices.length < 6 && (
              <Styles.AddChoiceButtonWrapper>
                <PrimaryButton
                  onClick={addChoice}
                  disabled={props.isLoading}
                >
                  {t`Add a response`}
                </PrimaryButton>
              </Styles.AddChoiceButtonWrapper>
            )}

            <Styles.PositiveLabel>
              {t`Most positive response`}
            </Styles.PositiveLabel>
          </Styles.ScaleContainer>
        )}

        <Styles.Warning>
          {t`Please note: if your content contains too many characters, it may not display properly`}
        </Styles.Warning>

        <Styles.Actions>
          <PrimaryButton
            onClick={props.onClose}
            disabled={props.isLoading}
            destructive
          >
            {t`Close`}
          </PrimaryButton>

          <PrimaryButton
            isLoading={props.isLoading}
            disabled={!canValidate() || !isDirty || props.isLoading}
            onClick={() => props.onValidate({
              choices: choices.map(choice => choice.trim()),
              name: name.trim(),
            })}
          >
            {t`Validate`}
          </PrimaryButton>
        </Styles.Actions>
      </Styles.Content>
    </Dialog>
  )

  function updateChoice(index: number, value: string) {
    setChoices(currentChoices =>
      currentChoices.map((choice, currentIndex) =>
        currentIndex === index ? value : choice,
      ),
    )
  }

  function addChoice() {
    setChoices(currentChoices => [ ...currentChoices, "" ])
  }

  function removeChoice(index: number) {
    setChoices(currentChoices =>
      currentChoices.filter((_, currentIndex) => currentIndex !== index),
    )
  }

  function getChoiceId(index: number): string {
    return `choice-${index}`
  }

  function getIndex(items: string[], id: UniqueIdentifier): number {
    return items.findIndex((_, index) => getChoiceId(index) === id)
  }

  function reorderChoices(
    choiceBeingDragged: Active,
    choiceBeingDraggedOver: Over | null,
  ): void {
    if (!choiceBeingDraggedOver) {
      return
    }

    const choiceDraggedIndex = getIndex(choices, choiceBeingDragged.id)
    const choiceDraggedOverIndex = getIndex(choices, choiceBeingDraggedOver.id)

    if (choiceDraggedIndex !== choiceDraggedOverIndex) {
      setChoices(currentChoices =>
        arrayMove(currentChoices, choiceDraggedIndex, choiceDraggedOverIndex),
      )
    }
  }
}

export interface QuestionDialogProps {
  customQuestion?: CustomQuestion,
  isLoading?: boolean,
  onClose: () => void,
  onValidate: (payload: CustomQuestionWithChoicesPayload) => void | Promise<void>,
}
